import FleetTitle from '@/libs/antd/components/Title';
import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import { Spin, Table, Typography } from 'antd';
import { FC } from 'react';
import dayjs from 'dayjs';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import { useGetMKLDetail } from '@/libs/fleet-api/services/mkl';
import { FleetFile } from '@/libs/fleet-api/fetcher';

type MKLPageProps = {
	mklId: number;
	onBack?: () => void;
};

const formatCurrency = (value: number | null | undefined) => {
	if (value === null || value === undefined) return '-';
	return new Intl.NumberFormat('id-ID').format(value);
};

const MKLPage: FC<MKLPageProps> = ({ mklId, onBack }) => {
	const { navigate } = useApp();
	const { openCustomerDetail, openPreviewModal } = useModals();

	const detailQuery = useGetMKLDetail({ mklId });
	const detail = detailQuery.data?.data;
	const isLoading = detailQuery.isLoading;
	const prospectIdFromPath = detail?.resolutionFile?.path?.split('/')?.[2];

	if (isLoading) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
				<Spin size="large" />
			</div>
		);
	}

	const infoItems: DataRowItem[] = [
		[
			[
				'Nama Customer',
				detail?.customerName ? (
					<a
						href="#"
						className="trigger-popup"
						onClick={(e) => {
							e.preventDefault();
							if (detail.customerId) openCustomerDetail(detail.customerId);
						}}
					>
						{detail.customerName}
					</a>
				) : undefined,
			],
			[
				'No.SPF',
				detail?.spfNo ? (
					<a
						href="#"
						className="trigger-popup"
						onClick={(e) => {
							e.preventDefault();
							navigate('SPFPage', {
								spfId: mklId,
								napResolutionId: detail?.napResolutionId ?? 0,
								onBack: () => navigate('MKLPage', { mklId, onBack }),
								viewOnly: true,
							});
						}}
					>
						{detail.spfNo}
					</a>
				) : undefined,
			],
		],
		[
			[
				'Group Customer BMPP',
				detail?.groupBmppName ? (
					<a
						href="#"
						className="trigger-popup"
						onClick={(e) => {
							e.preventDefault();
							navigate('FRMCustomerGroupListPage');
						}}
					>
						{detail.groupBmppName}
					</a>
				) : undefined,
			],
			[
				'History SPF',
				<a
					href="#"
					className="trigger-popup"
					onClick={(e) => {
						e.preventDefault();
						navigate('MKLHistorySPFPage', {
							spfId: mklId,
							onBack: () => navigate('MKLPage', { mklId, onBack }),
						});
					}}
				>
					View History
				</a>,
			],
		],
		[
			[
				'Summary NAP',
				prospectIdFromPath ? (
					<a
						href="#"
						onClick={(e) => {
							e.preventDefault();
							navigate('NAPPage', {
								prospectId: prospectIdFromPath,
								onBack: () => navigate('MKLPage', { mklId, onBack }),
							});
						}}
					>
						View Dokumen
					</a>
				) : (
					'-'
				),
			],
			[
				'Summary Risalah',
				detail?.resolutionFile ? (
					<a
						href="#"
						onClick={(e) => {
							e.preventDefault();
							openPreviewModal(detail.resolutionFile as FleetFile);
						}}
					>
						View Dokumen
					</a>
				) : (
					'-'
				),
			],
		],
	];

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
			<div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
				<div>
					{typeof onBack === 'function' && (
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={onBack}
						>
							<ArrowLeftOutlined />
						</button>
					)}
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle marginBottom={0}>Memo Kredit Limit</FleetTitle>
				</div>
			</div>

			<DataRow
				items={infoItems}
				loading={isLoading}
			/>

			<FleetTitle level={3} marginBottom={0}>
				Informasi Limit Pembiayaan Fleet
			</FleetTitle>

			<Table
				bordered
				scroll={{ x: 'max-content' }}
				pagination={false}
				dataSource={
					detail?.financingLimits?.map((item, index) => ({
						...item,
						key: index,
					})) ?? []
				}
				columns={[
					{
						title: 'No',
						align: 'center',
						width: 50,
						render: (_, __, index) => index + 1,
					},
					{
						title: 'No. MKL',
						dataIndex: 'mklNo',
						render: (value: string) => value || '-',
					},
					{
						title: 'Limit Pembiayaan',
						dataIndex: 'financingLimitAmount',
						render: (value: number) => formatCurrency(value),
					},
					{
						title: 'Sifat Limit',
						dataIndex: 'financingLimitNature',
						render: (value: string) => value || '-',
					},
					{
						title: 'Jenis Limit Pembiayaan',
						dataIndex: 'financingLimitType',
						render: (value: string) => value || '-',
					},
					{
						title: 'Expired Date',
						dataIndex: 'expiredDate',
						render: (value: string) =>
							value ? dayjs(value).format('DD/MM/YYYY') : '-',
					},
					{
						title: 'NTF',
						dataIndex: 'ntfAmount',
						render: (value: number | null) =>
							value === null || value === undefined ? (
								<Typography.Text type="secondary">-</Typography.Text>
							) : (
								formatCurrency(value)
							),
					},
					{
						title: 'OS Principal',
						dataIndex: 'outstandingPrincipalAmount',
						render: (value: number | null) =>
							value === null || value === undefined ? (
								<Typography.Text type="secondary">-</Typography.Text>
							) : (
								formatCurrency(value)
							),
					},
					{
						title: 'Sisa Limit',
						dataIndex: 'outstandingLimitAmount',
						render: (value: number | null) =>
							value === null || value === undefined ? (
								<Typography.Text type="secondary">-</Typography.Text>
							) : (
								formatCurrency(value)
							),
					},
				]}
			/>
		</div>
	);
};

export default MKLPage;
